import React from 'react';

import { Field } from 'formik';

import { IconButton, MenuItem } from '@material-ui/core';
import WarningIcon from '@mui/icons-material/Warning';
import ClearIcon from '@material-ui/icons/Clear';
import { Box } from '@mui/material';

import TranslationHelper from '../../../../helpers/TranslationHelper';
import FieldWrapper from '../../../../components/common/formikWrappers/FieldWrapper';
import FieldWithTooltip from '../../../../components/FieldWithTooltip';
import { DIGITS_ONLY_REGEX, DOUBLE_REGEX } from '../../../../constants/regex';
import { IAttributeDictionaryItem } from '../../state';
import { TMappedAttribute } from '../../types';
import { ATTRIBUTE_TYPES } from '../../constants';

interface IOwnProps {
    attribute: TMappedAttribute;
    error: string;
    setFieldValue: (fieldName: string, v: any) => void;
    label: string;
}

export const mapOptions = (
    optionsArray: IAttributeDictionaryItem[] = [],
    context = 'wx'
) => {
    const translated = optionsArray.map((item) => ({
        ...item,
        translatedLabel: TranslationHelper.translate(item.label, context),
    }));

    const sorted = translated.sort((a, b) =>
        a.translatedLabel.localeCompare(b.translatedLabel)
    );

    return sorted.map((item) => (
        <MenuItem key={item.name} value={item.name} disabled={!item.active}>
            {item.translatedLabel}
        </MenuItem>
    ));
};

export const InputComponent = ({
    attribute,
    error,
    setFieldValue,
    label,
}: IOwnProps) => {
    const { value, dictionary, name, required, type } = attribute;

    const selectable = !!dictionary?.length;
    const isInactiveValue = dictionary?.some(
        (dictValue) => !dictValue.active && dictValue.name === value
    );
    const availableOptions = dictionary?.filter((dictAttr) => {
        if (isInactiveValue) {
            return true;
        }
        return dictAttr.active || dictAttr.name === value;
    });

    const handleChange = (e: any) => {
        const val = e.target.value;
        const attrType = ATTRIBUTE_TYPES[type];

        if (
            val !== '' &&
            !selectable &&
            ((attrType === 'Double' && !DOUBLE_REGEX.test(val)) ||
                (attrType === 'Integer' && !DIGITS_ONLY_REGEX.test(val)))
        ) {
            return;
        }
        setFieldValue(`attributes.${attribute.name}.value`, val);
    };

    const handleClear = () => {
        setFieldValue(`attributes.${name}.value`, '');
    };
    const field = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'baseline',
                width: '100%',
            }}
        >
            <Field
                key={name}
                error={!!error}
                helperText={error}
                name={`attributes.${name}.value`}
                label={label}
                autoComplete={name}
                type="text"
                component={FieldWrapper}
                required={required}
                select={selectable}
                fullWidth
                onChange={handleChange}
            >
                {availableOptions?.length
                    ? mapOptions(availableOptions)
                    : mapOptions(dictionary)}
            </Field>
            {value && selectable && (
                <IconButton onClick={handleClear} size="small">
                    <ClearIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );

    return isInactiveValue ? (
        <FieldWithTooltip
            title="The selected value is no longer available"
            icon={<WarningIcon color="warning" />}
        >
            {field}
        </FieldWithTooltip>
    ) : (
        field
    );
};
